import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Building2, 
  MousePointerClick, 
  Users, 
  TrendingUp,
  Mail,
  Calendar,
  Loader2,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BVWaitlistSection = ({ password }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [password]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/bv-waitlist?password=${password}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching BV waitlist:", error);
      toast.error("Kon BV waitlist niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const conversionRate = data?.clicks > 0 
    ? ((data?.total / data?.clicks) * 100).toFixed(1) 
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <MousePointerClick className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-outfit font-medium text-zinc-900">{data?.clicks || 0}</p>
              <p className="text-xs text-zinc-500">CTA Clicks</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-outfit font-medium text-zinc-900">{data?.total || 0}</p>
              <p className="text-xs text-zinc-500">Waitlist Signups</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-outfit font-medium text-zinc-900">{conversionRate}%</p>
              <p className="text-xs text-zinc-500">Conversie</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-outfit font-medium text-zinc-900">BV Check</p>
              <p className="text-xs text-zinc-500">Coming Soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Building2 className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">BV Check Interesse Tracking</p>
            <p className="text-sm text-blue-700 mt-1">
              Deze data helpt bepalen of er voldoende interesse is om de BV Check te bouwen. 
              Gebruikers met verhoogd risico of twijfelgeval zien de BV upsell op hun resultaatpagina.
            </p>
          </div>
        </div>
      </div>

      {/* Waitlist Table */}
      <div className="bg-white border border-zinc-200/60 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-zinc-200/60 flex items-center justify-between">
          <h3 className="font-outfit font-medium text-zinc-900">Waitlist Aanmeldingen</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="text-zinc-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Vernieuwen
          </Button>
        </div>

        {!data?.entries || data.entries.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">Nog geen waitlist aanmeldingen</p>
            <p className="text-sm text-zinc-400 mt-1">
              Aanmeldingen verschijnen hier wanneer gebruikers zich inschrijven voor de BV Check
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-zinc-600 font-medium">E-mail</TableHead>
                <TableHead className="text-zinc-600 font-medium">Bron</TableHead>
                <TableHead className="text-zinc-600 font-medium">Datum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.entries.map((entry) => (
                <TableRow key={entry.id} className="hover:bg-zinc-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-zinc-400" />
                      <span className="font-medium text-zinc-900">{entry.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {entry.source === "result_page" ? "Resultaat pagina" : entry.source}
                    </span>
                  </TableCell>
                  <TableCell className="text-zinc-500 text-sm">
                    {formatDate(entry.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default BVWaitlistSection;
